// ============================================================================
// <CoordVerifyControl> — "did this pin put you at the right building?"
//
// Option C, 2026-08-05. The point of this control is that it is the ONLY honest
// source of coordinate trust we have.
//
// The Phase 3 dry-run appeared to prove 14 of 20 legacy coordinates were
// accurate. It proved nothing: the matches agreed to seven decimal places, which
// is the fingerprint of one source read twice — the stored coordinates were
// themselves Google output, so the test compared Google against Google. Ben had
// meanwhile physically driven to Old Tesson and ended up at a warehouse.
//
// Standing rule from that mistake: NEVER VALIDATE DATA AGAINST THE SERVICE THAT
// PRODUCED IT. Which leaves exactly one trustworthy validator — a person who
// went there. AMs already drive to these buildings every week; today that
// knowledge evaporates the moment they get back in the car. This captures it in
// one tap and feeds it straight into which URL the Navigate button builds.
//
// DESIGN
//  * Shown next to Navigate, because the moment you have just used the pin is
//    the only moment you know whether it worked.
//  * "Wrong" is as valuable as "right" and is equally easy to report. A
//    report-wrong immediately stops that coordinate being used for navigation
//    and notifies the map owners — it does not silently move anything.
//  * Never a blocking modal. A tech in a car park will not fill in a form.
// ============================================================================

import { useState } from 'react';
import { MapPinCheck, MapPinX, Check } from 'lucide-react';
import { supabase } from '../supabase';
import { errorMessage } from '../errors';

export type CoordVerifyResult = 'confirmed' | 'wrong';

/**
 * The report itself — fn_report_coord_verification. Shared with Ask AI's
 * building card (item 944). A note is sent only when one was given.
 */
export async function reportCoordVerification(
  facilityId: string,
  result: CoordVerifyResult,
  note?: string,
): Promise<{ error: { message: string } | null }> {
  const args: { p_facility_id: string; p_result: CoordVerifyResult; p_note?: string } = {
    p_facility_id: facilityId,
    p_result: result,
  };
  if (note && note.trim()) args.p_note = note.trim();
  const { error } = await supabase.rpc('fn_report_coord_verification', args);
  return { error };
}

export function CoordVerifyControl({
  facilityId,
  current,
  verifiedAt,
  onReported,
  className = '',
}: {
  facilityId: string;
  /** facilities.coord_verified_result — the standing report, if any. */
  current?: CoordVerifyResult | null;
  verifiedAt?: string | null;
  onReported?: (result: CoordVerifyResult) => void;
  className?: string;
}) {
  const [saving, setSaving] = useState<CoordVerifyResult | null>(null);
  const [done, setDone] = useState<CoordVerifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const settled = done ?? current ?? null;

  async function report(result: CoordVerifyResult) {
    setSaving(result);
    setError(null);
    const { error: rpcError } = await reportCoordVerification(facilityId, result);
    setSaving(null);
    if (rpcError) {
      // errorMessage, not `instanceof Error` — PostgrestError is NOT an Error.
      setError(errorMessage(rpcError));
      return;
    }
    setDone(result);
    onReported?.(result);
  }

  if (settled && !error) {
    const good = settled === 'confirmed';
    return (
      <div
        className={`flex items-center gap-1.5 text-xs ${
          good ? 'text-tcs-text-muted' : 'text-st-danger-fg'
        } ${className}`}
      >
        {good ? <Check className="h-3.5 w-3.5" /> : <MapPinX className="h-3.5 w-3.5" />}
        <span>
          {good ? 'Pin confirmed on site' : 'Pin reported wrong'}
          {verifiedAt && !done
            ? ` · ${new Date(verifiedAt).toLocaleDateString()}`
            : ''}
        </span>
        <button
          type="button"
          onClick={() => { setDone(null); setError(null); }}
          className="ml-1 underline underline-offset-2 hover:no-underline"
        >
          change
        </button>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <span className="text-xs text-tcs-text-muted">Did this pin get you to the door?</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={saving !== null}
          onClick={() => report('confirmed')}
          // 44px min touch target — this gets used on a phone, outdoors.
          className="flex h-11 items-center gap-1.5 rounded-lg border border-tcs-border px-3 text-xs font-semibold hover:bg-tcs-bg disabled:opacity-50"
        >
          <MapPinCheck className="h-4 w-4" />
          {saving === 'confirmed' ? 'Saving…' : 'Yes'}
        </button>
        <button
          type="button"
          disabled={saving !== null}
          onClick={() => report('wrong')}
          className="flex h-11 items-center gap-1.5 rounded-lg border border-st-danger-border-strong px-3 text-xs font-semibold text-st-danger-fg hover:bg-st-danger-bg disabled:opacity-50"
        >
          <MapPinX className="h-4 w-4" />
          {saving === 'wrong' ? 'Saving…' : 'No, wrong spot'}
        </button>
      </div>
      {error && <span className="text-xs text-st-danger-fg">{error}</span>}
    </div>
  );
}
